import type { ScratchCardFieldsFragment } from '#graphql-operations'
import type { ComputedRef } from 'vue'
import type { Composer } from 'vue-i18n'
import { canManualOpen, readScratchStore, scratchStoreVersion } from '~/utils/scratchStore'

/**
 * Scratch to win: the card this channel serves right now.
 *
 * `activeScratchCard` is admin content with no session behind it — every
 * visitor of a channel and language gets the same answer — so it is edge-cached
 * for an hour (`OPERATION_S_MAXAGE`) and fetched client-only, like the PDP's
 * gift promotions: nothing on the server-rendered page depends on it, and the
 * trigger cannot decide anything before hydration anyway.
 *
 * Cached per locale, because a locale IS a channel: the card, its copy and its
 * prize all follow the channel token. Concurrent callers (the trigger and the
 * announcement strip on the same page load) join one request.
 */

export type ActiveScratchCard = ScratchCardFieldsFragment

interface ScratchCardEntry {
  card: ActiveScratchCard | null
  fetchedAt: number
  /** A failed fetch, or a NOT_AVAILABLE answered by a mutation: refetch on the next ask. */
  stale: boolean
}

/** How long a fetched answer is reused, even once the card itself has expired. */
const ENTRY_TTL_MS = 5 * 60_000

/**
 * The requests in flight, shared by every caller. Module-scoped for the
 * promises (a Promise cannot go through the SSR payload, and this only ever
 * runs in the browser), keyed by locale.
 */
const inflight = new Map<string, Promise<ActiveScratchCard | null>>()

/** Live = no endsAt, or endsAt (exclusive) still in the future. */
export function isScratchCardLive(card: ActiveScratchCard | null | undefined, nowMs: number): card is ActiveScratchCard {
  if (!card)
    return false
  const end = typeof card.endsAt === 'string' ? Date.parse(card.endsAt) : Number.NaN
  return Number.isNaN(end) || end > nowMs
}

export function useScratchCard(): {
  card: ComputedRef<ActiveScratchCard | null>
  ensure: (options?: { force?: boolean }) => Promise<ActiveScratchCard | null>
  invalidate: () => void
} {
  const entries = useState<Record<string, ScratchCardEntry>>('scratch-card', () => ({}))
  // Read off the plugin rather than `useI18n()`, so `ensure()` is callable from
  // outside component setup (the trigger's watchers, the `?code=` carrier).
  const i18n = useNuxtApp().$i18n as Composer | undefined
  const localeOf = () => String(i18n?.locale?.value ?? 'en')

  // The card can end while a page is left open, and the edge copy lags a rule's
  // end by up to its TTL, so the verdict is re-checked every minute — the same
  // guard `useGiftPromotions` applies to a promotion's `endsAt`.
  const now = useNow({ interval: 60_000 })

  const card = computed<ActiveScratchCard | null>(() => {
    const entry = entries.value[localeOf()]
    return isScratchCardLive(entry?.card, now.value.getTime()) ? entry!.card : null
  })

  async function ensure(options: { force?: boolean } = {}): Promise<ActiveScratchCard | null> {
    if (!import.meta.client)
      return null

    const locale = localeOf()
    const entry = entries.value[locale]
    const at = Date.now()

    if (entry && !options.force) {
      // A card that is still live (or a channel that genuinely has none) needs
      // no second look.
      if (!entry.stale && (entry.card === null || isScratchCardLive(entry.card, at)))
        return entry.card
      // An expired card, or a fetch that failed: answer from the cache for a
      // few minutes rather than hammering the edge on every navigation.
      if (at - entry.fetchedAt < ENTRY_TTL_MS)
        return isScratchCardLive(entry.card, at) ? entry.card : null
    }

    const pending = inflight.get(locale)
    if (pending)
      return pending

    const request = (async () => {
      try {
        const { data } = await useGraphqlQuery('activeScratchCard')
        const fetched = (data?.activeScratchCard ?? null) as ActiveScratchCard | null
        entries.value = { ...entries.value, [locale]: { card: fetched, fetchedAt: Date.now(), stale: false } }
        return isScratchCardLive(fetched, Date.now()) ? fetched : null
      }
      catch (error) {
        console.error('activeScratchCard failed', error)
        entries.value = { ...entries.value, [locale]: { card: null, fetchedAt: Date.now(), stale: true } }
        return null
      }
      finally {
        inflight.delete(locale)
      }
    })()

    inflight.set(locale, request)
    return request
  }

  /** The server has just refused this card (NOT_AVAILABLE): stop trusting the cached copy. */
  function invalidate(): void {
    const locale = localeOf()
    const entry = entries.value[locale]
    if (entry)
      entries.value = { ...entries.value, [locale]: { ...entry, stale: true } }
  }

  return { card, ensure, invalidate }
}

/**
 * True on the client once a card is servable AND this visitor may still open it
 * — what turns the announcement strip's newsletter line into the scratch line.
 *
 * SSR and hydration both render `false`, deliberately: the strip is in
 * CDN-cached HTML, so the scratch text would be a hydration mismatch (and a
 * promise to visitors who have already played). Component setup only.
 *
 * `active` says whether the caller has an offer to show at all — for the bar,
 * whether it carries a newsletter message. Only then is the card fetched early,
 * after mount: that fetch exists so the first click on the offer opens the
 * scratcher at once (`useScratchFlowCore.requestManualOpen`), and with nothing
 * to click it is a request per full load bought for nobody. `ScratchTrigger`'s
 * own prompt does not depend on it: the trigger fetches the card itself.
 * Inactive, the offer stays `false`. Omitted, it is always active.
 */
export function useScratchStripOffer(active: () => boolean = () => true): ComputedRef<boolean> {
  const mounted = ref(false)
  const { card, ensure } = useScratchCard()
  const auth = useAuth()

  onMounted(() => {
    mounted.value = true
    // Registered here, so even its immediate run is post-mount: `ensure()`
    // fetches in the browser only. It follows `active`, which for the bar can
    // turn true later, when the layout mounts after a `layout: false` page and
    // fetches the bar on the client.
    watch(active, (isActive) => {
      if (isActive)
        void ensure()
    }, { immediate: true })
  })

  return computed(() => {
    if (!mounted.value || !active() || !card.value)
      return false
    // Touch the write counter so a dismissal or a completed card re-evaluates.
    void scratchStoreVersion.value
    if (auth?.activeCustomer.value?.phoneNumber?.trim())
      return false
    return canManualOpen(readScratchStore(), String(card.value.id))
  })
}
